import Link from "next/link";
import {
  buildDestinationJobHref,
  type DestinationBridge,
} from "@/lib/destinations/bridge";
import type { DestinationBridgeJobSummary } from "@/lib/destinations/bridge-counts";

type Props = {
  bridge: DestinationBridge;
  jobSummary?: DestinationBridgeJobSummary | null;
  showConditionLink?: boolean;
  showFestivalLink?: boolean;
  heading?: string;
  intro?: string;
  compact?: boolean;
};

function jobHeading(
  bridge: DestinationBridge,
  jobSummary?: DestinationBridgeJobSummary | null,
): string {
  if (!jobSummary) return `${bridge.name} jobs`;
  if (jobSummary.count === null) return "Check current jobs";
  if (jobSummary.count === 0) return bridge.jobFilter.emptyHeading;
  const label =
    jobSummary.count === 1
      ? bridge.jobFilter.resultLabelSingular
      : bridge.jobFilter.resultLabelPlural;
  return `${jobSummary.count.toLocaleString("en-GB")} live ${label}`;
}

function jobBody(
  bridge: DestinationBridge,
  jobSummary?: DestinationBridgeJobSummary | null,
): string {
  if (!jobSummary) return bridge.jobFilter.emptyBody;
  if (jobSummary.count === null) {
    return "We could not check the live count just now, but the filtered jobs page is ready.";
  }
  if (jobSummary.count === 0) return bridge.jobFilter.emptyBody;
  if (bridge.jobFilter.specificity === "role") {
    return "This count comes from the role filters, not a Croyde-only marketplace location.";
  }
  return "This count comes from active listings matching the bridge filter.";
}

function BridgeCard({
  eyebrow,
  title,
  body,
  href,
  cta,
  tone = "light",
}: {
  eyebrow: string;
  title: string;
  body: string;
  href: string;
  cta: string;
  tone?: "light" | "dark";
}) {
  const dark = tone === "dark";
  return (
    <Link
      href={href}
      className={`block rounded-[14px] border p-5 no-underline transition-all hover:-translate-y-0.5 ${
        dark
          ? "border-navy bg-navy text-white hover:bg-navy-hover"
          : "border-border-light bg-white text-charcoal hover:border-gold/30 hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)]"
      }`}
    >
      <div
        className={`text-[11px] font-semibold uppercase tracking-[1.2px] ${
          dark ? "text-gold" : "text-gold"
        }`}
      >
        {eyebrow}
      </div>
      <div
        className={`mt-1 font-serif text-[18px] font-semibold leading-[1.2] ${
          dark ? "text-white" : "text-navy"
        }`}
      >
        {title}
      </div>
      <p
        className={`mt-2 text-[12.5px] leading-[1.55] ${
          dark ? "text-white/75" : "text-text-muted"
        }`}
      >
        {body}
      </p>
      <div
        className={`mt-3 text-[12.5px] font-semibold ${
          dark ? "text-gold" : "text-navy"
        }`}
      >
        {cta} <span aria-hidden>&rarr;</span>
      </div>
    </Link>
  );
}

export function DestinationBridgeSection({
  bridge,
  jobSummary,
  showConditionLink = false,
  showFestivalLink = true,
  heading,
  intro,
  compact = false,
}: Props) {
  const jobsHref = jobSummary?.href ?? buildDestinationJobHref(bridge);
  const planningRoute =
    bridge.plannerRoute ?? (showFestivalLink ? bridge.festivalRoute : null);
  const planningLabel =
    bridge.plannerLabel ?? (showFestivalLink ? bridge.festivalLabel : null);

  return (
    <section
      className={`max-w-[1100px] mx-auto px-5 md:px-12 ${
        compact ? "py-6 md:py-8" : "py-10 md:py-14"
      }`}
    >
      <div className="mb-5 md:mb-6">
        <div className="text-[11px] font-semibold uppercase tracking-[2px] text-gold mb-2">
          Destination bridge
        </div>
        <h2 className="font-serif text-[22px] md:text-[28px] font-bold text-navy leading-[1.15]">
          {heading ?? `${bridge.name}: conditions, work and crew`}
        </h2>
        <p className="mt-2 max-w-[680px] text-[14px] leading-[1.65] text-text-muted">
          {intro ?? bridge.contextSummary}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {showConditionLink && bridge.conditionRoute && (
          <BridgeCard
            eyebrow="Conditions"
            title={`Live ${bridge.name} conditions`}
            body="Check the current score, forecast and season context before you plan around this place."
            href={bridge.conditionRoute}
            cta="View conditions"
          />
        )}

        <BridgeCard
          eyebrow="Work"
          title={jobHeading(bridge, jobSummary)}
          body={jobBody(bridge, jobSummary)}
          href={jobsHref}
          cta="Browse jobs"
          tone="dark"
        />

        {bridge.guideRoute && bridge.guideLabel && (
          <BridgeCard
            eyebrow="Guide"
            title={bridge.guideLabel}
            body="Read the practical route into this season, including roles, timing and local context."
            href={bridge.guideRoute}
            cta="Read guide"
          />
        )}

        <BridgeCard
          eyebrow="Crew"
          title={bridge.crew.label}
          body="Find the relevant crew search and see who is heading that way."
          href={bridge.crew.route}
          cta="Find crews"
        />

        {planningRoute && planningLabel && (
          <BridgeCard
            eyebrow={bridge.plannerRoute ? "Plan" : "Festival"}
            title={planningLabel}
            body={
              bridge.plannerRoute
                ? "Use the planner entry point for the next practical decision."
                : "Open the festival weather and packing page for this event."
            }
            href={planningRoute}
            cta={bridge.plannerRoute ? "Plan next" : "View festival"}
          />
        )}
      </div>
    </section>
  );
}
